
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { motion } from "framer-motion";
import { ChevronRight, Pen, Trophy, Info } from "lucide-react";

export default function AboutPage() {
  const [typedText, setTypedText] = useState("");
  const fullText = "Create. Compete. Perfect Your Craft.";
  
  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setTypedText(fullText.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 70);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-32 pb-20 px-4">
        <div className="container max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <motion.h1 
              className="text-4xl md:text-6xl lg:text-7xl font-bold font-heading mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-primary/80"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              VibeWriter
            </motion.h1>
            
            <motion.h2 
              className="text-xl md:text-2xl text-white/80 h-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.7 }}
            >
              {typedText}
            </motion.h2>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center mt-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7 }}
            >
              <Button 
                asChild
                size="lg" 
                className="bg-gradient-to-r from-primary to-secondary text-white shadow-lg group"
              >
                <Link to="/" className="flex items-center">
                  <Pen className="mr-2 h-5 w-5" />
                  Start Writing
                  <ChevronRight className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              
              <Button 
                asChild
                size="lg" 
                variant="secondary"
                className="bg-white/10 hover:bg-white/15 text-white"
              >
                <Link to="/leaderboard" className="flex items-center">
                  <Trophy className="mr-2 h-5 w-5" />
                  View Leaderboard
                </Link>
              </Button>
            </motion.div>
          </div>
          
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.7 }}
          >
            <FeatureCard 
              title="Daily Prompts" 
              description="New writing challenges every day across multiple categories to keep your creative juices flowing."
              icon={<Pen className="h-10 w-10 text-primary" />}
            />
            <FeatureCard 
              title="Compete & Rank" 
              description="Submit your writing and see how you rank against other vibrant creators on the leaderboard."
              icon={<Trophy className="h-10 w-10 text-primary" />}
            />
            <FeatureCard 
              title="Improve Your Craft" 
              description="Get feedback, study the best submissions, and elevate your writing skills over time."
              icon={<Info className="h-10 w-10 text-primary" />}
            />
          </motion.div>
        </div>
      </main>
      
      <footer className="border-t border-white/10 py-8">
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-white/60 text-sm">
                © 2023 VibeWriter. All rights reserved.
              </p>
            </div>
            <div className="flex space-x-6">
              <a href="#" className="text-white/60 hover:text-white transition-colors">Terms</a>
              <a href="#" className="text-white/60 hover:text-white transition-colors">Privacy</a>
              <a href="#" className="text-white/60 hover:text-white transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

interface FeatureCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

function FeatureCard({ title, description, icon }: FeatureCardProps) {
  return (
    <div className="glass-card p-6 rounded-xl hover:bg-card/90 transition-colors group">
      <div className="mb-4">{icon}</div>
      <h3 className="text-xl font-semibold mb-2 font-heading">{title}</h3>
      <p className="text-white/70">{description}</p>
    </div>
  );
}
